<template>
    <div v-if="show">
            <div class="audio-player-container">
                <!-- پلیر صوت اصلی -->
                <audio ref="audioPlayer" :src="audioUrl" controls controlsList="nodownload" class="audio-player"
                    @loadedmetadata="onAudioLoaded" @error="onAudioError">
                    مرورگر شما از پخش فایل صوتی پشتیبانی نمی‌کند.
                </audio>

                <!-- اطلاعات فایل -->
                <div class="audio-info">
                    <div class="info-item">
                        <font-awesome-icon :icon="['fas', 'file-audio']" class="info-icon" />
                        <span class="label">نام فایل:</span>
                        <span class="value">{{ fileName }}</span>
                    </div>
                    <div class="info-item">
                        <font-awesome-icon :icon="['fas', 'format']" class="info-icon" />
                        <span class="label">فرمت:</span>
                        <span class="value">{{ fileFormat }}</span>
                    </div>
                    <div class="info-item" v-if="duration">
                        <font-awesome-icon :icon="['fas', 'clock']" class="info-icon" />
                        <span class="label">مدت زمان:</span>
                        <span class="value">{{ formatTime(duration) }}</span>
                    </div>
                    <div class="info-item" v-if="fileSize">
                        <font-awesome-icon :icon="['fas', 'weight-hanging']" class="info-icon" />
                        <span class="label">حجم فایل:</span>
                        <span class="value">{{ formatFileSize(fileSize) }}</span>
                    </div>
                </div>

                <!-- کنترل‌های پیشرفته -->
                <div class="advanced-controls">
                    <button class="control-btn play-pause-btn" @click="togglePlay" :disabled="!audioLoaded"
                        :class="{ playing: isPlaying }">
                        <font-awesome-icon :icon="['fas', isPlaying ? 'pause' : 'play']" class="btn-icon" />
                        <span>{{ isPlaying ? 'توقف' : 'پخش' }}</span>
                    </button>

                    <button class="control-btn stop-btn" @click="stopAudio" :disabled="!audioLoaded">
                        <font-awesome-icon :icon="['fas', 'stop']" class="btn-icon" />
                        <span>توقف کامل</span>
                    </button>

                    <button class="control-btn restart-btn" @click="restartAudio"
                        :disabled="!audioLoaded || !isPlaying">
                        <font-awesome-icon :icon="['fas', 'redo']" class="btn-icon" />
                        <span>شروع مجدد</span>
                    </button>

                    <button class="control-btn download-btn" @click="downloadAudio">
                        <font-awesome-icon :icon="['fas', 'download']" class="btn-icon" />
                        <span>دانلود</span>
                    </button>
                </div>

                <!-- نوار پیشرفت -->
                <div class="progress-container" v-if="duration">
                    <div class="progress-bar" @click="seekAudio">
                        <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                    </div>
                    <div class="time-display">
                        <span>{{ formatTime(currentTime) }}</span>
                        <span>/</span>
                        <span>{{ formatTime(duration) }}</span>
                    </div>
                </div>
            </div>

            <!-- وضعیت لودینگ -->
            <div v-if="loading" class="loading-state">
                <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
                <p>در حال بارگذاری فایل صوتی...</p>
            </div>

            <!-- وضعیت خطا -->
            <div v-if="error" class="error-state">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error-icon" />
                <p>{{ error }}</p>
                <button @click="retryLoad" class="retry-btn">
                    <font-awesome-icon :icon="['fas', 'redo']" class="btn-icon" />
                    <span>تلاش مجدد</span>
                </button>
            </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

// eslint-disable-next-line no-undef
const props = defineProps({
    show: Boolean,
    audioData: Object
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['close'])

// رفرنس‌ها
const audioPlayer = ref(null)

// حالت‌ها
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const audioLoaded = ref(false)
const loading = ref(false)
const error = ref('')

// محاسبه شده‌ها
const title = computed(() => props.audioData?.name || 'فایل صوتی')
const audioUrl = computed(() => props.audioData?.url)
const fileName = computed(() => props.audioData?.name || 'نامشخص')
// const fileFormat = computed(() => {
//     const ext = fileName.value.split('.').pop()
//     return ext.toUpperCase()
// })
const fileFormat = computed(() => props.audioData?.contentType)

const fileSize = computed(() => props.audioData?.fileSize)

const progressPercent = computed(() => {
    if (!duration.value) return 0
    return (currentTime.value / duration.value) * 100
})

// متدها (همان متدهای قبلی)
const closeModal = () => {
    stopAudio()
    emit('close')
}

const onAudioLoaded = () => {
    const audio = audioPlayer.value
    if (audio) {
        duration.value = audio.duration
        audioLoaded.value = true
        loading.value = false
        error.value = ''
    }
}

const onAudioError = (e) => {
    console.error('Audio loading error:', e)
    error.value = 'خطا در بارگذاری فایل صوتی'
    loading.value = false
}

const togglePlay = () => {
    const audio = audioPlayer.value
    if (!audio) return

    if (isPlaying.value) {
        audio.pause()
    } else {
        audio.play().catch(err => {
            console.error('Play error:', err)
            error.value = 'خطا در پخش فایل صوتی'
        })
    }
    isPlaying.value = !isPlaying.value
}

const stopAudio = () => {
    const audio = audioPlayer.value
    if (audio) {
        audio.pause()
        audio.currentTime = 0
        isPlaying.value = false
    }
}

const restartAudio = () => {
    const audio = audioPlayer.value
    if (audio) {
        audio.currentTime = 0
        if (!isPlaying.value) {
            audio.play()
            isPlaying.value = true
        }
    }
}

const seekAudio = (event) => {
    const audio = audioPlayer.value
    if (!audio || !duration.value) return

    const progressBar = event.currentTarget
    const clickPosition = event.offsetX
    const progressBarWidth = progressBar.clientWidth
    const seekTime = (clickPosition / progressBarWidth) * duration.value

    audio.currentTime = seekTime
}

const updateTime = () => {
    const audio = audioPlayer.value
    if (audio) {
        currentTime.value = audio.currentTime
    }
}

const downloadAudio = () => {
    if (props.audioData?.url) {
        const link = document.createElement('a')
        link.href = props.audioData.url
        link.download = props.audioData.name
        link.click()
    }
}

const retryLoad = () => {
    error.value = ''
    loading.value = true
    const audio = audioPlayer.value
    if (audio) {
        audio.load()
    }
}

// توابع فرمت‌کننده
const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00'

    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const formatFileSize = (bytes) => {
    if (!bytes) return 'نامشخص'

    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

// واتچرها و لایف‌سایکل (همانند قبل)
watch(() => props.audioData, (newData) => {
    if (newData) {
        loading.value = true
        audioLoaded.value = false
        error.value = ''

        currentTime.value = 0
        duration.value = 0
        isPlaying.value = false
    }
}, { immediate: true })

onMounted(() => {
    const audio = audioPlayer.value
    if (audio) {
        audio.addEventListener('timeupdate', updateTime)
        audio.addEventListener('ended', () => {
            isPlaying.value = false
        })
    }
})

onUnmounted(() => {
    const audio = audioPlayer.value
    if (audio) {
        audio.removeEventListener('timeupdate', updateTime)
        audio.removeEventListener('ended', () => { })
    }

    if (audioUrl.value) {
        URL.revokeObjectURL(audioUrl.value)
    }
})
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    padding: 0;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    background: #f8f9fa;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 10px;
}

.title-icon {
    color: #007bff;
    font-size: 1.2rem;
}

.modal-header h3 {
    margin: 0;
    color: #333;
    font-size: 1.2rem;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #666;
    padding: 8px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    color: #333;
    background: #e0e0e0;
}

.audio-player-container {
    padding: 20px;
}

.audio-player {
    width: 100%;
    margin-bottom: 20px;
    border-radius: 8px;
}

.audio-info {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.info-item {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-icon {
    color: #666;
    width: 16px;
    font-size: 0.9rem;
}

.label {
    font-weight: bold;
    color: #555;
    min-width: 80px;
}

.value {
    color: #333;
    flex: 1;
}

.advanced-controls {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    justify-content: center;
}

.control-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.2s;
    min-width: 120px;
    justify-content: center;
}

.control-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.control-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.play-pause-btn.playing {
    background: #28a745;
    color: white;
    border-color: #28a745;
}

.play-pause-btn:not(.playing):hover:not(:disabled) {
    background: #28a745;
    color: white;
    border-color: #28a745;
}

.stop-btn:hover:not(:disabled) {
    background: #dc3545;
    color: white;
    border-color: #dc3545;
}

.restart-btn:hover:not(:disabled) {
    background: #ffc107;
    color: #212529;
    border-color: #ffc107;
}

.download-btn:hover:not(:disabled) {
    background: #007bff;
    color: white;
    border-color: #007bff;
}

.btn-icon {
    width: 16px;
    font-size: 0.9rem;
}

.progress-container {
    margin-top: 15px;
}

.progress-bar {
    width: 100%;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    cursor: pointer;
    position: relative;
    margin-bottom: 8px;
}

.progress-fill {
    height: 100%;
    background: #007bff;
    border-radius: 3px;
    transition: width 0.1s;
}

.time-display {
    display: flex;
    justify-content: space-between;
    font-size: 0.8rem;
    color: #666;
}

.loading-state,
.error-state {
    text-align: center;
    padding: 40px 20px;
}

.loading-icon {
    font-size: 2rem;
    color: #007bff;
    margin-bottom: 15px;
}

.error-icon {
    font-size: 2rem;
    color: #dc3545;
    margin-bottom: 15px;
}

.retry-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    margin-top: 15px;
}

.retry-btn:hover {
    background: #0056b3;
}
</style>